import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import ReactMarkdown from 'react-markdown';
import api from '../api/api';
import { trackABTestEvent } from '../utils/abTesting';
import '../styles/pages/IngredientInputPage.css';

// よく使う食材リスト（スーパーで実際に売っている形）
const COMMON_INGREDIENTS = {
  meat: [
    '鶏むね肉', '鶏もも肉', '鶏ささみ', '手羽元', '手羽先',
    '豚こま切れ', '豚バラ薄切り', '豚ロース', '豚ひき肉',
    '牛こま切れ', '牛バラ薄切り', '牛ひき肉',
    '合いびき肉', 'ベーコン', 'ウインナー'
  ],
  fish: [
    '鮭切り身', 'さば切り身', 'ぶり切り身', 'たら切り身',
    'さんま', 'あじ', 'いわし',
    'エビ', 'イカ', 'ツナ缶', 'サバ缶'
  ],
  vegetable: [
    'キャベツ', '玉ねぎ', 'にんじん', 'じゃがいも',
    'ピーマン', 'トマト', 'なす', 'きゅうり',
    'ほうれん草', '小松菜', 'ブロッコリー', '白菜',
    '大根', 'ねぎ', 'レタス', 'もやし',
    'しめじ', 'えのき', 'エリンギ', 'しいたけ'
  ],
  other: [
    '卵', '木綿豆腐', '絹ごし豆腐', '厚揚げ', '油揚げ',
    '納豆', 'チーズ', 'バター', '牛乳', 'ヨーグルト',
    'こんにゃく', 'ちくわ', 'はんぺん'
  ]
};

/**
 * IngredientInputPage - 食材入力→レシピ生成
 * メイン機能1: 冷蔵庫の中身を入力してレシピを提案
 * - ボタン選択（メイン）
 * - 手動入力（補助）
 * - 写真撮影
 */
const IngredientInputPage = () => {
  const { user, plan } = useAuth();
  const navigate = useNavigate();

  // 食材選択
  const [ingredientCategory, setIngredientCategory] = useState('meat'); // 'meat' | 'fish' | 'vegetable' | 'other'
  const [ingredientList, setIngredientList] = useState([]);

  // 手動入力
  const [manualInput, setManualInput] = useState('');

  // 食事タイプ選択
  const [mealType, setMealType] = useState('おまかせ'); // 'おまかせ' | '朝食' | '昼食' | '夕食'

  // レシピ難易度選択
  const [recipeDifficulty, setRecipeDifficulty] = useState('easy'); // 'super_easy' | 'easy' | 'detailed'

  // レシピ生成
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);
  const [savedRecipeIds, setSavedRecipeIds] = useState([]);
  const [copiedRecipeIndex, setCopiedRecipeIndex] = useState(null);

  // 調味料設定
  const [userSeasonings, setUserSeasonings] = useState([]);
  const [showSeasoningBanner, setShowSeasoningBanner] = useState(false);

  const isPremium = plan === 'premium';
  const isStandard = plan === 'standard';
  const canUsePhotoFeature = isStandard || isPremium;

  // ページタイトル設定
  useEffect(() => {
    document.title = '冷蔵庫レシピ | つむぎ';
  }, []);

  // 使用状況と調味料設定を取得
  useEffect(() => {
    fetchUsage();
    fetchRecipeUsage();
    fetchSeasonings();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await api.get('/api/fridge/usage');
      if (response.data.success) {
        // setFridgeUsage(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const fetchRecipeUsage = async () => {
    try {
      const response = await api.get('/api/recipes/usage');
      if (response.data.success) {
        // setRecipeUsage(response.data.usage);
      }
    } catch (error) {
      console.error('Failed to fetch recipe usage:', error);
    }
  };

  const fetchSeasonings = async () => {
    try {
      const response = await api.get('/api/seasonings');
      if (response.data.success) {
        const seasonings = response.data.seasoningsList || [];
        setUserSeasonings(seasonings);

        // 調味料が未設定ならバナー表示
        if (seasonings.length === 0) {
          setShowSeasoningBanner(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch seasonings:', error);
    }
  };

  // ボタンから食材を追加/削除（トグル）
  const toggleIngredient = (ingredient) => {
    if (ingredientList.includes(ingredient)) {
      // 既に選択済み → 削除
      setIngredientList(ingredientList.filter(item => item !== ingredient));
    } else {
      // 未選択 → 追加
      setIngredientList([...ingredientList, ingredient]);
    }
  };

  // 手動入力から食材リストを作成
  const handleManualInputSubmit = () => {
    if (!manualInput.trim()) return;

    // カンマまたは改行で分割
    const ingredients = manualInput
      .split(/[,、\n]/)
      .map(item => item.trim())
      .filter(item => item.length > 0);

    // 既存のリストに追加（重複は除外）
    const newIngredients = ingredients.filter(item => !ingredientList.includes(item));
    setIngredientList([...ingredientList, ...newIngredients]);
    setManualInput(''); // 入力欄をクリア
  };


  // レシピをシェア（Web Share API使用）
  const handleShareRecipe = async (recipeId, recipeTitle, recipeIndex) => {
    try {
      const shareUrl = `${window.location.origin}/recipe/${recipeId}`;

      // Web Share APIに対応しているかチェック
      if (navigator.share) {
        await navigator.share({
          title: recipeTitle || 'つむぎのレシピ提案',
          text: `${recipeTitle}のレシピをシェアします`,
          url: shareUrl
        });
        setCopiedRecipeIndex(recipeIndex);
        setTimeout(() => setCopiedRecipeIndex(null), 2000);
      } else {
        // 非対応の場合はクリップボードにコピー（フォールバック）
        await navigator.clipboard.writeText(shareUrl);
        setCopiedRecipeIndex(recipeIndex);
        setTimeout(() => setCopiedRecipeIndex(null), 2000);
      }
    } catch (err) {
      // ユーザーがキャンセルした場合などはエラーを無視
      if (err.name !== 'AbortError') {
        console.error('シェアに失敗しました:', err);
      }
    }
  };

  // レシピ生成
  const handleGenerateRecipe = async () => {
    if (ingredientList.length === 0) {
      alert('食材を1つ以上入力してください');
      return;
    }

    // Track recipe generation start
    if (user?._id) {
      trackABTestEvent('RECIPE_NAMING', user._id, 'recipe_generation_start', {
        ingredientCount: ingredientList.length,
        method: 'manual_selection'
      });
    }

    setIsGenerating(true);

    try {
      // 新しいレシピ生成APIを使用
      const preferences = userSeasonings ? ['調味料: ' + userSeasonings.join('、')] : [];

      // 食事タイプを追加（おまかせ以外の場合）
      if (mealType !== 'おまかせ') {
        preferences.push(`食事タイプ: ${mealType}`);
      }

      // アレルギー・食事制限を取得
      const restrictions = [];
      if (user?.profile?.allergies && user.profile.allergies.length > 0) {
        restrictions.push('アレルギー: ' + user.profile.allergies.join('、'));
      }
      if (user?.profile?.allergiesOther) {
        restrictions.push('アレルギー（その他）: ' + user.profile.allergiesOther);
      }
      if (user?.profile?.avoidFoods && user.profile.avoidFoods.length > 0) {
        restrictions.push('避けたい食材: ' + user.profile.avoidFoods.join('、'));
      }
      if (user?.profile?.avoidFoodsOther) {
        restrictions.push('避けたい食材（その他）: ' + user.profile.avoidFoodsOther);
      }

      const response = await api.post('/api/recipes/generate', {
        ingredients: ingredientList,
        preferences,
        dietaryRestrictions: restrictions.join('、'),
        mealType: mealType !== 'おまかせ' ? mealType : undefined,
        recipeDifficulty: recipeDifficulty
      });

      if (response.data.success) {
        setGeneratedRecipe(response.data.recipes); // recipesはMarkdown文字列
        // setRecipeCost(response.data.cost);

        // レシピ表示後にページトップへスクロール
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Track recipe generation complete
        if (user?._id) {
          trackABTestEvent('RECIPE_NAMING', user._id, 'recipe_generation_complete', {
            ingredientCount: ingredientList.length,
            method: 'manual_selection',
            success: true
          });
        }

        // 生成されたレシピを自動保存
        const ids = await saveGeneratedRecipes(response.data.recipes);
        setSavedRecipeIds(ids);
      }
    } catch (error) {
      console.error('Recipe generation failed:', error);

      // Track failure
      if (user?._id) {
        trackABTestEvent('RECIPE_NAMING', user._id, 'recipe_generation_complete', {
          ingredientCount: ingredientList.length,
          method: 'manual_selection',
          success: false,
          error: error.response?.status === 429 ? 'quota_exceeded' : 'generation_failed'
        });
      }

      if (error.response?.status === 429) {
        alert('今月は上限までご利用いただきました。来月またご利用いただけます。');
      } else {
        alert('レシピの生成に失敗しました。もう一度お試しください。');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // 生成されたレシピを自動保存
  const saveGeneratedRecipes = async (recipesMarkdown) => {
    try {
      // Markdownからレシピを分割（## で始まる見出しで分割）
      const recipeBlocks = recipesMarkdown.split(/(?=##\s+\d+\.)/).filter(block => block.trim());
      const savedIds = [];

      for (const block of recipeBlocks) {
        // タイトルを抽出（## 1. タイトル の形式）
        const titleMatch = block.match(/##\s+\d+\.\s+(.+)/);
        if (!titleMatch) continue;

        const title = titleMatch[1].trim();

        // タグを自動抽出（拡張版）
        const autoTags = [];
        const lowerBlock = block.toLowerCase();
        const lowerTitle = title.toLowerCase();

        // 朝食・昼食・夕食の検出
        if (lowerBlock.includes('朝食') || lowerBlock.includes('朝ごはん') || lowerBlock.includes('breakfast')) {
          autoTags.push('朝食');
        }
        if (lowerBlock.includes('昼食') || lowerBlock.includes('ランチ') || lowerBlock.includes('lunch')) {
          autoTags.push('昼食');
        }
        if (lowerBlock.includes('夕食') || lowerBlock.includes('晩ごはん') || lowerBlock.includes('dinner')) {
          autoTags.push('夕食');
        }

        // ヨウ素控えめ関連（橋本病専門アプリなので常に付与）
        if (lowerBlock.includes('甲状腺') || lowerBlock.includes('橋本病') || lowerBlock.includes('セレン') || lowerBlock.includes('ヨウ素')) {
          autoTags.push('ヨウ素控えめ');
        }

        // 簡単・時短
        if (lowerBlock.includes('簡単') || lowerBlock.includes('時短') || lowerBlock.includes('手軽') || lowerTitle.includes('簡単')) {
          autoTags.push('簡単');
        }

        // 保存API呼び出し
        const response = await api.post('/api/recipes/save', {
          title,
          content: block.trim(),
          sourceConsultationId: null, // レシピ専用機能では null
          tags: autoTags
        });

        // レシピIDを取得
        if (response.data.success && response.data.recipe?._id) {
          savedIds.push(response.data.recipe._id);
        }
      }

      console.log(`✅ ${recipeBlocks.length}件のレシピを保存しました`);
      return savedIds;
    } catch (error) {
      console.error('レシピ保存エラー:', error);
      return [];
    }
  };

  return (
    <AppLayout>
      <PageTitle
        title="今ある食材でレシピ"
        subtitle="買い物不要、疲れた日もすぐ作れる"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      <div className="ingredient-input-page max-w-[600px] mx-auto p-4">
        {/* ローディングオーバーレイ */}
        {isGenerating && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-white/95 backdrop-blur-sm flex items-center justify-center z-[9999]">
            <div className="bg-gradient-to-br from-mint/10 to-honey/10 border-2 border-mint/30 p-12 px-14 rounded-3xl text-center shadow-2xl max-w-md">
              {/* つむぎ画像 */}
              <div className="mb-4 flex justify-center">
                <img
                  src="/tsumugi.png"
                  alt="つむぎ"
                  className="ingredient-input-page__tsumugi-image animate-bounce"
                />
              </div>
              <div className="w-[60px] h-[60px] border-[5px] border-gray-200 border-t-mint border-r-honey rounded-full mx-auto mb-6 animate-spin"></div>
              <p className="text-xl font-semibold text-gray-800 mb-3 tracking-wide">
                つむぎがレシピを考えています...
              </p>
              <p className="text-[0.95rem] text-gray-600 mb-6 leading-relaxed">
                栄養バランスを考えながら、美味しい献立を作っています
              </p>
              <div className="flex justify-center gap-2">
                <span className="w-2.5 h-2.5 bg-mint rounded-full animate-bounce"></span>
                <span className="w-2.5 h-2.5 bg-honey rounded-full animate-bounce delay-75"></span>
                <span className="w-2.5 h-2.5 bg-peach rounded-full animate-bounce delay-150"></span>
              </div>
            </div>
          </div>
        )}
        {/* 調味料設定バナー（初回のみ） */}
        {showSeasoningBanner && !generatedRecipe && (
          <div className="ingredient-input-page__seasoning-banner flex items-start gap-4 p-6 bg-gradient-to-br from-yellow-100 to-yellow-50 border-2 border-yellow-300 rounded-xl mb-6">
            <div className="ingredient-input-page__seasoning-icon text-3xl shrink-0">⚠️</div>
            <div className="ingredient-input-page__seasoning-content flex-1">
              <h4 className="ingredient-input-page__seasoning-title text-base font-bold text-gray-800 mb-2">調味料を設定してください</h4>
              <p className="ingredient-input-page__seasoning-text text-sm text-yellow-900 mb-4 leading-normal">
                設定しないと使えない調味料が含まれるレシピが生成される可能性があります
              </p>
              <div className="ingredient-input-page__seasoning-actions flex gap-3 flex-wrap">
                <button
                  className="tsumugi-btn tsumugi-btn-primary"
                  onClick={() => navigate('/seasonings-settings')}
                >
                  今すぐ設定する
                </button>
                <button
                  className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray"
                  onClick={() => setShowSeasoningBanner(false)}
                >
                  後で
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Photo-first UI for Standard/Premium users */}
        {!generatedRecipe && canUsePhotoFeature && (
          <div className="ingredient-input-page__photo-first mb-4">
            {/* PRIMARY: Photo capture - シンプル版 */}
            <div
              className="ingredient-input-page__photo-card tsumugi-card tsumugi-card--mint cursor-pointer hover:shadow-lg transition-all duration-200 mb-6"
              onClick={() => {
                // Track photo capture start
                if (user?._id) {
                  trackABTestEvent('RECIPE_NAMING', user._id, 'photo_capture_start', {
                    userPlan: plan
                  });
                }
                navigate('/fridge-photo');
              }}
            >
              <div className="flex items-center gap-4">
                <div className="text-4xl">📷</div>
                <div className="flex-1">
                  <h3 className="tsumugi-card-title text-base mb-1">冷蔵庫を撮影してレシピ生成</h3>
                  <p className="text-hint text-sm">写真を撮るだけ、食材を自動認識</p>
                </div>
                <div className="text-2xl text-gray-400">›</div>
              </div>
            </div>

            {/* SECONDARY: Manual selection - 常に表示 */}
            <div className="tsumugi-card tsumugi-card--shell mb-4">
              <h3 className="tsumugi-card-title">🔍 または、食材を手動で選ぶ</h3>
              <p className="text-hint text-sm mb-3">
                タップで選択・解除できます ※1つからOK！
              </p>

            {/* 食事タイプ選択 */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">🍽️ 食事タイプ</h4>
              <div className="flex gap-2 overflow-x-auto">
                {['おまかせ', '朝食', '昼食', '夕食'].map((type) => (
                  <button
                    key={type}
                    className={`shrink-0 py-2 px-4 rounded-full text-sm font-semibold transition-all duration-200 ${
                      mealType === type
                        ? 'bg-gradient-to-br from-honey to-peach text-white border-2 border-honey'
                        : 'bg-white border-2 border-gray-300 text-gray-600 hover:border-honey/50'
                    }`}
                    onClick={() => setMealType(type)}
                  >
                    {type === 'おまかせ' && '⭐ '}
                    {type}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 「おまかせ」は時間帯に合わせたレシピを提案します
              </p>
            </div>

            {/* レシピの詳しさ選択 */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                🎚️ レシピの詳しさ <span className="text-xs text-gray-500">（橋本病患者向け）</span>
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '12px',
                  border: `2px solid ${recipeDifficulty === 'super_easy' ? '#10b981' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: recipeDifficulty === 'super_easy' ? '#f0fdf4' : 'transparent',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="recipeDifficulty"
                    value="super_easy"
                    checked={recipeDifficulty === 'super_easy'}
                    onChange={(e) => setRecipeDifficulty(e.target.value)}
                    style={{ marginRight: '10px', marginTop: '2px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>とにかく簡単（5-10分）</div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      倦怠感が強い方向け。包丁・まな板不要。ゆで卵+納豆など
                    </div>
                  </div>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '12px',
                  border: `2px solid ${recipeDifficulty === 'easy' ? '#10b981' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: recipeDifficulty === 'easy' ? '#f0fdf4' : 'transparent',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="recipeDifficulty"
                    value="easy"
                    checked={recipeDifficulty === 'easy'}
                    onChange={(e) => setRecipeDifficulty(e.target.value)}
                    style={{ marginRight: '10px', marginTop: '2px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>簡単レシピ（15-20分）【おすすめ】</div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      バランス重視。5-7種類の食材で継続しやすい
                    </div>
                  </div>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '12px',
                  border: `2px solid ${recipeDifficulty === 'detailed' ? '#10b981' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: recipeDifficulty === 'detailed' ? '#f0fdf4' : 'transparent',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="recipeDifficulty"
                    value="detailed"
                    checked={recipeDifficulty === 'detailed'}
                    onChange={(e) => setRecipeDifficulty(e.target.value)}
                    style={{ marginRight: '10px', marginTop: '2px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>こだわりレシピ（30分以上）</div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      料理好きな方向け。栄養価を最優先した詳細レシピ
                    </div>
                  </div>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                ⚠️ 橋本病の倦怠感を考慮し、デフォルトは「簡単レシピ」です
              </p>
            </div>

            {/* カテゴリタブ */}
            <div className="ingredient-input-page__category-tabs flex gap-2 mb-6 overflow-x-auto">
              <button
                className={`ingredient-input-page__category-tab shrink-0 py-3 px-6 bg-white border-2 border-gray-300 rounded-full text-[0.9375rem] font-semibold text-gray-600 cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-green-200 hover:bg-green-200/10 ${ingredientCategory === 'meat' ? 'ingredient-input-page__category-tab--active border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                onClick={() => setIngredientCategory('meat')}
              >
                🍖 肉
              </button>
              <button
                className={`ingredient-input-page__category-tab shrink-0 py-3 px-6 bg-white border-2 border-gray-300 rounded-full text-[0.9375rem] font-semibold text-gray-600 cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-green-200 hover:bg-green-200/10 ${ingredientCategory === 'fish' ? 'ingredient-input-page__category-tab--active border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                onClick={() => setIngredientCategory('fish')}
              >
                🐟 魚
              </button>
              <button
                className={`ingredient-input-page__category-tab shrink-0 py-3 px-6 bg-white border-2 border-gray-300 rounded-full text-[0.9375rem] font-semibold text-gray-600 cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-green-200 hover:bg-green-200/10 ${ingredientCategory === 'vegetable' ? 'ingredient-input-page__category-tab--active border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                onClick={() => setIngredientCategory('vegetable')}
              >
                🥬 野菜
              </button>
              <button
                className={`ingredient-input-page__category-tab shrink-0 py-3 px-6 bg-white border-2 border-gray-300 rounded-full text-[0.9375rem] font-semibold text-gray-600 cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-green-200 hover:bg-green-200/10 ${ingredientCategory === 'other' ? 'ingredient-input-page__category-tab--active border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                onClick={() => setIngredientCategory('other')}
              >
                🥚 その他
              </button>
            </div>

            {/* 食材ボタングリッド */}
            <div className="ingredient-input-page__grid grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-3 mb-4">
              {COMMON_INGREDIENTS[ingredientCategory].map((ingredient) => (
                <button
                  key={ingredient}
                  className={`ingredient-input-page__button relative py-3 px-4 bg-white border-2 border-gray-300 rounded-xl text-[0.9375rem] font-semibold text-gray-800 cursor-pointer transition-all duration-200 text-center hover:border-green-200 hover:bg-green-200/10 hover:-translate-y-0.5 ${ingredientList.includes(ingredient) ? 'ingredient-input-page__button--selected border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                  onClick={() => toggleIngredient(ingredient)}
                >
                  {ingredient}
                  {ingredientList.includes(ingredient) && <span className="ingredient-input-page__button-check absolute top-1 right-1 text-sm">✓</span>}
                </button>
              ))}
            </div>

            {/* 選択中の食材数 */}
            <div className="ingredient-input-page__selected-count text-center text-sm font-semibold text-gray-800 mt-4">
              選択中: {ingredientList.length}個
            </div>

            {/* レシピ生成ボタン */}
            <button
              className="tsumugi-btn tsumugi-btn-primary w-full mt-4"
              onClick={handleGenerateRecipe}
              disabled={isGenerating || ingredientList.length === 0}
            >
              {isGenerating ? 'レシピ生成中...' : 'レシピを作る'}
            </button>

            {/* 手動入力エリア（見つからない食材用） */}
            <div className="ingredient-input-page__manual-area mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2 font-semibold">📝 見つからない食材は手入力できます</p>
              <input
                type="text"
                className="tsumugi-form-input w-full"
                placeholder="例: 鶏もも肉、玉ねぎ、にんじん（カンマ区切りでEnter）"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleManualInputSubmit();
                  }
                }}
                onBlur={() => {
                  // 入力欄から離れたら自動追加
                  if (manualInput.trim()) {
                    handleManualInputSubmit();
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-1">💡 カンマ区切りで複数入力OK。Enterキーまたは他の場所をタップすると追加されます</p>
            </div>
            </div>
          </div>
        )}

        {/* Manual-first UI for Free users */}
        {!generatedRecipe && !canUsePhotoFeature && (
          <div className="ingredient-input-page__manual-first">
            {/* Upgrade CTA Banner */}
            <div className="ingredient-input-page__upgrade-banner tsumugi-card mb-4 bg-gradient-to-br from-yellow-100 to-yellow-50 border-2 border-yellow-300">
              <div className="flex items-start gap-3 mb-3">
                <div className="text-4xl">✨</div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800 text-base mb-2">写真で簡単レシピ生成</h3>
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    スタンダードプラン以上なら、冷蔵庫を撮影するだけで食材を自動認識してレシピを提案します
                  </p>
                  <button
                    className="tsumugi-btn tsumugi-btn-primary"
                    onClick={() => {
                      // Track plan upgrade view
                      if (user?._id) {
                        trackABTestEvent('RECIPE_NAMING', user._id, 'plan_upgrade_view', {
                          source: 'recipe_photo_cta',
                          currentPlan: plan
                        });
                      }
                      navigate('/plans');
                    }}
                  >
                    プランを見る
                  </button>
                </div>
              </div>
            </div>

            {/* Manual ingredient selection for Free users */}
            <div className="tsumugi-card tsumugi-card--mint mb-4">
              <h3 className="tsumugi-card-title">🔍 冷蔵庫にある食材を選んでください</h3>
              <p className="text-hint text-sm mb-3">
                タップで選択・解除できます ※1つからOK！
              </p>

              {/* 食事タイプ選択 */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">🍽️ 食事タイプ</h4>
                <div className="flex gap-2 overflow-x-auto">
                  {['おまかせ', '朝食', '昼食', '夕食'].map((type) => (
                    <button
                      key={type}
                      className={`shrink-0 py-2 px-4 rounded-full text-sm font-semibold transition-all duration-200 ${
                        mealType === type
                          ? 'bg-gradient-to-br from-honey to-peach text-white border-2 border-honey'
                          : 'bg-white border-2 border-gray-300 text-gray-600 hover:border-honey/50'
                      }`}
                      onClick={() => setMealType(type)}
                    >
                      {type === 'おまかせ' && '⭐ '}
                      {type}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 「おまかせ」は時間帯に合わせたレシピを提案します
                </p>
              </div>

              {/* レシピの詳しさ選択 */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  🎚️ レシピの詳しさ <span className="text-xs text-gray-500">（橋本病患者向け）</span>
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '12px',
                    border: `2px solid ${recipeDifficulty === 'super_easy' ? '#10b981' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: recipeDifficulty === 'super_easy' ? '#f0fdf4' : 'transparent',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="recipeDifficultyFree"
                      value="super_easy"
                      checked={recipeDifficulty === 'super_easy'}
                      onChange={(e) => setRecipeDifficulty(e.target.value)}
                      style={{ marginRight: '10px', marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>とにかく簡単（5-10分）</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        倦怠感が強い方向け。包丁・まな板不要。ゆで卵+納豆など
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '12px',
                    border: `2px solid ${recipeDifficulty === 'easy' ? '#10b981' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: recipeDifficulty === 'easy' ? '#f0fdf4' : 'transparent',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="recipeDifficultyFree"
                      value="easy"
                      checked={recipeDifficulty === 'easy'}
                      onChange={(e) => setRecipeDifficulty(e.target.value)}
                      style={{ marginRight: '10px', marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>簡単レシピ（15-20分）【おすすめ】</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        バランス重視。5-7種類の食材で継続しやすい
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '12px',
                    border: `2px solid ${recipeDifficulty === 'detailed' ? '#10b981' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: recipeDifficulty === 'detailed' ? '#f0fdf4' : 'transparent',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="recipeDifficultyFree"
                      value="detailed"
                      checked={recipeDifficulty === 'detailed'}
                      onChange={(e) => setRecipeDifficulty(e.target.value)}
                      style={{ marginRight: '10px', marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>こだわりレシピ（30分以上）</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        料理好きな方向け。栄養価を最優先した詳細レシピ
                      </div>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ⚠️ 橋本病の倦怠感を考慮し、デフォルトは「簡単レシピ」です
                </p>
              </div>

              {/* Category tabs (duplicate for Free users) */}
              <div className="ingredient-input-page__category-tabs flex gap-2 mb-6 overflow-x-auto">
                <button
                  className={`ingredient-input-page__category-tab shrink-0 py-3 px-6 bg-white border-2 border-gray-300 rounded-full text-[0.9375rem] font-semibold text-gray-600 cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-green-200 hover:bg-green-200/10 ${ingredientCategory === 'meat' ? 'ingredient-input-page__category-tab--active border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                  onClick={() => setIngredientCategory('meat')}
                >
                  🍖 肉
                </button>
                <button
                  className={`ingredient-input-page__category-tab shrink-0 py-3 px-6 bg-white border-2 border-gray-300 rounded-full text-[0.9375rem] font-semibold text-gray-600 cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-green-200 hover:bg-green-200/10 ${ingredientCategory === 'fish' ? 'ingredient-input-page__category-tab--active border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                  onClick={() => setIngredientCategory('fish')}
                >
                  🐟 魚
                </button>
                <button
                  className={`ingredient-input-page__category-tab shrink-0 py-3 px-6 bg-white border-2 border-gray-300 rounded-full text-[0.9375rem] font-semibold text-gray-600 cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-green-200 hover:bg-green-200/10 ${ingredientCategory === 'vegetable' ? 'ingredient-input-page__category-tab--active border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                  onClick={() => setIngredientCategory('vegetable')}
                >
                  🥬 野菜
                </button>
                <button
                  className={`ingredient-input-page__category-tab shrink-0 py-3 px-6 bg-white border-2 border-gray-300 rounded-full text-[0.9375rem] font-semibold text-gray-600 cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-green-200 hover:bg-green-200/10 ${ingredientCategory === 'other' ? 'ingredient-input-page__category-tab--active border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                  onClick={() => setIngredientCategory('other')}
                >
                  🥚 その他
                </button>
              </div>

              {/* Ingredient grid (duplicate for Free users) */}
              <div className="ingredient-input-page__grid grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-3 mb-4">
                {COMMON_INGREDIENTS[ingredientCategory].map((ingredient) => (
                  <button
                    key={ingredient}
                    className={`ingredient-input-page__button relative py-3 px-4 bg-white border-2 border-gray-300 rounded-xl text-[0.9375rem] font-semibold text-gray-800 cursor-pointer transition-all duration-200 text-center hover:border-green-200 hover:bg-green-200/10 hover:-translate-y-0.5 ${ingredientList.includes(ingredient) ? 'ingredient-input-page__button--selected border-green-200 bg-gradient-to-br from-green-200 to-green-300 text-white' : ''}`}
                    onClick={() => toggleIngredient(ingredient)}
                  >
                    {ingredient}
                    {ingredientList.includes(ingredient) && <span className="ingredient-input-page__button-check absolute top-1 right-1 text-sm">✓</span>}
                  </button>
                ))}
              </div>

              {/* Selected count */}
              <div className="ingredient-input-page__selected-count text-center text-sm font-semibold text-gray-800 mt-4">
                選択中: {ingredientList.length}個
              </div>

              {/* Recipe generation button */}
              <button
                className="tsumugi-btn tsumugi-btn-primary w-full mt-4"
                onClick={handleGenerateRecipe}
                disabled={isGenerating || ingredientList.length === 0}
              >
                {isGenerating ? 'レシピ生成中...' : 'レシピを作る'}
              </button>

              {/* 手動入力エリア（見つからない食材用） */}
              <div className="ingredient-input-page__manual-area mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2 font-semibold">📝 見つからない食材は手入力できます</p>
                <input
                  type="text"
                  className="tsumugi-form-input w-full"
                  placeholder="例: 鶏もも肉、玉ねぎ、にんじん（カンマ区切りでEnter）"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleManualInputSubmit();
                    }
                  }}
                  onBlur={() => {
                    // 入力欄から離れたら自動追加
                    if (manualInput.trim()) {
                      handleManualInputSubmit();
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">💡 カンマ区切りで複数入力OK。Enterキーまたは他の場所をタップすると追加されます</p>
              </div>
            </div>
          </div>
        )}

        {/* 生成されたレシピ */}
        {generatedRecipe && (
          <div>
            <div className="ingredient-input-page__recipe-header mb-3">
              <h3 className="text-lg font-bold text-gray-800">🍳 つむぎのレシピ提案</h3>
            </div>
            <div className="ingredient-input-page__recipe-content">
              {/* レシピを分割して表示 */}
              {generatedRecipe.split(/(?=##\s+\d+\.\s)/).filter(recipe => recipe.trim()).map((recipe, index) => {
                // レシピタイトルを抽出
                const titleMatch = recipe.match(/##\s+\d+\.\s+(.+)/);
                const recipeTitle = titleMatch ? titleMatch[1].trim() : 'つむぎのレシピ';
                const recipeId = savedRecipeIds[index];
                const isCopied = copiedRecipeIndex === index;

                return (
                  <div key={index} className="recipe-card">
                    {/* 各レシピのシェアボタン */}
                    <div className="flex justify-end mb-3">
                      <button
                        className={`ingredient-input-page__share-btn py-2 px-4 bg-transparent text-green-200 border-[1.5px] border-green-200 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-300 whitespace-nowrap hover:bg-green-200 hover:text-white hover:-translate-y-0.5 hover:shadow-md hover:shadow-black/10 ${isCopied ? 'ingredient-input-page__share-btn--shared bg-green-600 text-white border-green-600 hover:bg-green-700 hover:border-green-700' : ''}`}
                        onClick={() => handleShareRecipe(recipeId, recipeTitle, index)}
                        title="LINE・メール・SNSなどでレシピをシェア"
                        disabled={!recipeId}
                      >
                        {isCopied ? '✓ シェアしました！' : '📤 シェア'}
                      </button>
                    </div>

                    <div className="ingredient-input-page__recipe-markdown">
                      <ReactMarkdown
                        components={{
                          h2: ({node, ...props}) => (
                            <h2 className="recipe-title" {...props} />
                          ),
                          h3: ({node, ...props}) => (
                            <h3 className="recipe-section-title" {...props} />
                          ),
                          ul: ({node, ...props}) => (
                            <ul className="recipe-list" {...props} />
                          ),
                          ol: ({node, ...props}) => (
                            <ol className="recipe-steps" {...props} />
                          ),
                          li: ({node, ordered, ...props}) => (
                            <li className={ordered ? 'recipe-step-item' : 'recipe-list-item'} {...props} />
                          ),
                          p: ({node, ...props}) => (
                            <p className="recipe-text" {...props} />
                          ),
                          strong: ({node, ...props}) => (
                            <strong className="recipe-highlight" {...props} />
                          ),
                        }}
                      >
                        {recipe}
                      </ReactMarkdown>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* コスト情報はバックエンドログで確認 */}

            <button
              className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--mint w-full mt-4"
              onClick={() => {
                setGeneratedRecipe(null);
                // setRecipeCost(null);
                setIngredientList([]);
                setManualInput('');
                setSavedRecipeIds([]);
                setCopiedRecipeIndex(null);
              }}
            >
              新しいレシピを考える
            </button>
          </div>
        )}

        {/* 注意書き */}
        <div className="ingredient-input-page__disclaimer mt-8 p-4 text-center">
          <p className="text-hint text-xs">
            ※ このサービスは医療行為ではありません。一般的な栄養の知恵をもとにした献立提案です。
          </p>
        </div>
      </div>
    </AppLayout>
  );
};

export default IngredientInputPage;
